import {useContext, useState} from "react";
import {ApiContext} from "../../util/Requests";
import SimpleEditableCard from "./SimpleEditableCard";
import EditableEntryInput from "../editable_entries/EditableEntryInput";
import {SC_Subcontractor} from "../../util/api/crews/SC_Subcontractor";
import SimpleEvenFlex from "./SimpleEvenFlex";
import EditableCardEntryStateMultiselect from "../editable_entries/EditableCardEntryStateMultiselect";
import {SC_CrewType} from "../../util/api/enums/SC_CrewType";
import EditableCardEntryUser from "../editable_entries/EditableCardEntryUser";
import EditableCardEntryUserMultiselect from "../editable_entries/EditableCardEntryUserMultiselect";
import EditableCardEntryAddress from "../editable_entries/EditableCardEntryAddress";
import SubcontractorFilesCard from "./SubcontractorFilesCard";
import TabsSelector from "../filters/TabsSelector";
import EditableCardEntryInput from "../editable_entries/EditableCardEntryInput";
import EditableCardEntryPhone from "../editable_entries/EditableCardEntryPhone";
import {SC_CrewfromAPI} from "../../util/api/crews/CrewUtils";
import FontAwesomeButton from "./FontAwesomeButton";
import EditCardRibbon from "../EditCardRibbon";
import SimpleIconsPlate from "./SimpleIconsPlate";
import PermissionsRequired from "./PermissionsRequired";
import {FontAwesomeButtonEnumTypes} from "./FontAwesomeButtonEnumTypes";
import {faBoxArchive} from "@fortawesome/free-solid-svg-icons/faBoxArchive";
import {faBoxOpen} from "@fortawesome/free-solid-svg-icons/faBoxOpen";
import {SC_Address} from "../../util/api/SC_Address";
import EditableEntry from "../editable_entries/EditableEntry";
import UserPreviewModal from "../modals/previews/UserPreviewModal";
import EditableCardEntryEmail from "../editable_entries/EditableCardEntryEmail";

/**
 * @param {SC_CrewBase} crew
 * @constructor
 */
const CrewCard = ({crew, creatingNew = false, onCancel = () => {}, onSave = () => {}}) => {
    const {caches, doRequest} = useContext(ApiContext);

    const [isEditing, setIsEditing] = useState(creatingNew);

    const [displayCrew, setDisplayCrew] = useState(crew.clone());
    const [editingCrew, setEditingCrew] = useState(displayCrew.clone());
    const [cachedCrew, setCachedCrew] = useState(displayCrew.clone());

    function saveCrew(tractor) {
        let formData = new FormData();

        tractor.applyToFormData(formData, !creatingNew ? displayCrew : null);

        return doRequest(formData, "modify_crew").then(v => {
            if (v.code === 200) {
                let cru = SC_CrewfromAPI(v.reason);
                caches.crews.update();
                setIsEditing(false);
                onSave();
                setEditingCrew(cru.clone());
                setDisplayCrew(cru.clone());
                setCachedCrew(cru.clone());
            }
        })
    }

    function toggleArchive() {
        let confirmMessage = `Are you sure you want to ${editingCrew.active ? "archive" : "un-archive"} ${editingCrew.alias}?`;

        if (!confirm(confirmMessage))
            return;

        editingCrew.active = !editingCrew.active;

        saveCrew(editingCrew);
    }

    const managers = crew.lead?.recursiveManagers ?? [];

    return (
        <SimpleEditableCard isEditing={isEditing}>
            <SimpleEvenFlex growUp={false}>
                <div>
                    <EditableEntryInput
                        isEditing={isEditing}
                        type={"text"}
                        placeholder={`${editingCrew.crewType.toString()} Name`}
                        baseValue={editingCrew.alias}
                        // baseValue={<><span style={{verticalAlign: "center", paddingRight: "0.5rem"}}>{editingCrew.alias}</span><FontAwesomeButton icon={faExternalLink} href={`/neo-list-workorders/?crew_id=${editingCrew.internalID}`}/></>}
                        onEdit={(v) => {
                            const mb = editingCrew.clone();
                            mb.alias = v;
                            setEditingCrew(mb);
                        }}
                    />
                </div>
                <PermissionsRequired writeCrews={true}>
                    <SimpleIconsPlate>
                        {!isEditing ? <FontAwesomeButton icon={editingCrew.active ? faBoxArchive : faBoxOpen} title={(editingCrew.active ? "Archive" : "Un-archive") + " this " + (editingCrew.isSub ? "sub" : "crew")} buttonType={new FontAwesomeButtonEnumTypes({
                            enabled: true,
                        })} onClick={() => toggleArchive()}/> : null}
                        <EditCardRibbon discrete={false} flatten={true} onSave={() => {
                            saveCrew(editingCrew)
                        }} isEditing={isEditing} onCancel={() => {
                            onCancel();
                            setCachedCrew(editingCrew.clone());
                            setEditingCrew(displayCrew.clone());
                            setIsEditing(false);
                        }} onEdit={() => {
                            setEditingCrew(cachedCrew.clone());
                            setIsEditing(true);
                        }}/>
                    </SimpleIconsPlate>
                </PermissionsRequired>
            </SimpleEvenFlex>

            {editingCrew.crewType.is(SC_CrewType.SUB) ?
                <SimpleEvenFlex>
                    <EditableCardEntryInput
                        baseValue={editingCrew.companyName}
                        displayLabel={"Official Name"}
                        isEditing={isEditing}
                        type={"text"}
                        onEdit={(v) => {
                            const mb = editingCrew.clone();
                            mb.merge(new SC_Subcontractor({companyName: v}));
                            setEditingCrew(mb);
                        }}
                    />
                </SimpleEvenFlex> : null}

            <SimpleEvenFlex>
                <EditableCardEntryStateMultiselect displayLabel={"State(s) Serviced"} isEditing={isEditing} baseValue={editingCrew.states} onEdit={(v) => {
                    const mb = editingCrew.clone();
                    mb.states = v;
                    setEditingCrew(mb);
                }}/>
                {crew.crewType.is(SC_CrewType.SUB) ? <>
                    <EditableCardEntryAddress
                        isEditing={isEditing}
                        baseValue={editingCrew.mailingAddress}
                        displayLabel={"Mailing Address"}
                        prefix={"Mailing "}
                        latlon={false}
                        timezone={false}
                        onEdit={(a) => {
                            console.log("Edited", a);
                            const mb = editingCrew.clone();
                            mb.mailingAddress.merge(new SC_Address(a));
                            setEditingCrew(mb);
                        }}
                    />
                    <EditableCardEntryAddress
                        isEditing={isEditing}
                        baseValue={editingCrew.officeAddress}
                        displayLabel={"Office Address"}
                        prefix={"Office "}
                        latlon={false}
                        timezone={false}
                        onEdit={(a) => {
                            const mb = editingCrew.clone();
                            mb.officeAddress = a;
                            setEditingCrew(mb);
                        }}
                    />
                </> : null}
            </SimpleEvenFlex>
            {editingCrew.crewType.is(SC_CrewType.SUB) ?
                <SimpleEvenFlex extraStyle={{marginTop: "0.5rem"}}>
                <EditableCardEntryInput baseValue={editingCrew.coiexpiry ? editingCrew.coiexpiry.getUTCDay() + "/" + (editingCrew.coiexpiry.getUTCMonth() + 1) + "/" + editingCrew.coiexpiry.getUTCFullYear() : "N/A"} type={"date"} isEditing={false} displayLabel={"COI Expiration"}/>
                <EditableCardEntryInput baseValue={editingCrew.officeContactName} type={"text"} onEdit={(n) => {
                    const mb = editingCrew.clone();
                    mb.officeContactName = n;
                    setEditingCrew(mb);
                }} isEditing={isEditing} displayLabel={"Contact Name"} />
                <div>
                    <EditableCardEntryEmail baseValue={editingCrew.officeContactEmail} onEdit={(e) => {
                        const mb = editingCrew.clone();
                        mb.officeContactEmail = e;
                        setEditingCrew(mb);
                    }} isEditing={isEditing} displayLabel={"Contact Email"} hyperlinkDisplay={true}/>
                    <EditableCardEntryPhone baseValue={editingCrew.officeContactPhone} onEdit={(p) => {
                        const mb = editingCrew.clone();
                        mb.officeContactPhone = p;
                        setEditingCrew(mb);
                    }} isEditing={isEditing} displayLabel={"Contact Phone"}/>
                </div>
            </SimpleEvenFlex> : <>
                <SimpleEvenFlex extraStyle={{marginBottom: "-0.75rem"}}>
                    <EditableCardEntryUser fancy={true} isEditing={isEditing} displayLabel={"Lead User"}
                                           baseValue={editingCrew.leadID} onEdit={(l) => {
                        const mb = editingCrew.clone();
                        mb.leadID = l;
                        setEditingCrew(mb);
                    }} filterFunction={(user) => user.isCrewLead && user.name}/>
                </SimpleEvenFlex>
                <SimpleEvenFlex extraStyle={{marginBottom: "-0.75rem"}}>
                    <EditableCardEntryUserMultiselect fancy={true} isEditing={isEditing} displayLabel={"Member Users"}
                                                      baseValue={editingCrew.memberIDs} onEdit={(l) => {
                        const mb = editingCrew.clone();
                        mb.memberIDs = l;
                        setEditingCrew(mb);
                    }}/>
                </SimpleEvenFlex>
                </>}
            {!isEditing ? <SimpleEvenFlex>
                <EditableEntry isEditing={false} viewingChildren={<div>
                    {managers.length ? managers.map(it => <div><UserPreviewModal user={it} fancy={true} /></div>) : <i>No Managers</i>}
                </div>} label={"Managers"} />
            </SimpleEvenFlex> : null}
            <TabsSelector children={[
                {
                    title: "Sub Files",
                    content: <SubcontractorFilesCard files={editingCrew.files} sub={editingCrew} callReload={(v) => {
                        const newContractor = SC_Subcontractor.fromAPI(v);

                        setCachedCrew(newContractor);
                        setDisplayCrew(newContractor);
                        setEditingCrew(newContractor);
                    }}/>,
                    display: editingCrew.crewType.is(SC_CrewType.SUB)
                }
            ]}/>
        </SimpleEditableCard>
    )
}

export default CrewCard;